import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCircleCheck, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import InlineNotice from './InlineNotice';
import { toMessageList, pickDatasetFields, categorizeFieldTypes } from '../utils/datasetSummary';

const TYPE_LABELS = { csv: 'CSV', excel: 'Excel' };

// Read-only review of what the backend actually analyzed — every value
// here is labeled backend data, nothing is authored beyond unit labels.
export default function UploadAnalysisScreen({ connection, onStartChat, onBack }) {
  const dsSource = (connection?.dataset_summary && typeof connection.dataset_summary === 'object')
    ? connection.dataset_summary
    : connection;
  const fields = pickDatasetFields(dsSource) || {};
  const { rows, columns, worksheets, tables, quality, detectedTypes } = fields;
  const recommendations = fields.recommendations?.length ? fields.recommendations : toMessageList(connection?.recommendations);
  const warnings = connection?.file_is_unreliable
    ? [...toMessageList(connection?.warnings), 'This file has inconsistent formatting (some rows have different column counts). Results may be inaccurate.']
    : toMessageList(connection?.warnings);
  const suggestedQuestions = Array.isArray(connection?.suggested_questions)
    ? connection.suggested_questions.filter(q => typeof q === 'string' && q.trim())
    : [];
  const fieldTypeCounts = categorizeFieldTypes(detectedTypes);

  const fileName = connection?.file_name || connection?.original_filename || connection?.name || 'Your file';
  const connectionLabel = TYPE_LABELS[String(connection?.type || '').toLowerCase()] || connection?.type || '—';

  const stats = [
    worksheets != null ? { label: 'Worksheets', value: worksheets } : null,
    tables != null ? { label: 'Tables', value: tables } : null,
    rows != null ? { label: 'Records', value: Number(rows).toLocaleString() } : null,
    columns != null ? { label: 'Columns', value: columns } : null,
  ].filter(Boolean);

  return (
    <div style={s.page}>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          style={s.backBtn}
          aria-label="Go back"
        >
          <FontAwesomeIcon icon={faArrowLeft} style={s.backIcon} />
          <span>Back</span>
        </button>
      )}

      <div style={s.content}>
        {/* Success header */}
        <div style={s.successCard}>
          <div style={s.successIconWrap}>
            <FontAwesomeIcon icon={faCircleCheck} style={s.successIcon} aria-hidden="true" />
          </div>
          <div>
            <h1 style={s.successTitle}>Dataset uploaded successfully</h1>
            <p style={s.successSub}>
              Your dataset has been analyzed and is ready for AI-powered querying.
            </p>
          </div>
        </div>

        {/* Meta stats */}
        <div style={s.metaGrid}>
          <div style={s.metaItem}>
            <div style={s.metaLabel}>File Name</div>
            <div style={s.metaValue} title={fileName}>{fileName}</div>
          </div>
          <div style={s.metaItem}>
            <div style={s.metaLabel}>Connection</div>
            <div style={s.metaValue}>{connectionLabel}</div>
          </div>
          {stats.map(stat => (
            <div key={stat.label} style={s.metaItem}>
              <div style={s.metaLabel}>{stat.label}</div>
              <div style={s.metaValue}>{stat.value}</div>
            </div>
          ))}
          {quality != null && (
            <div style={s.metaItem}>
              <div style={s.metaLabel}>Quality Score</div>
              <div style={s.metaValue}>{quality}</div>
            </div>
          )}
        </div>

        {/* Detected Fields */}
        {fieldTypeCounts.length > 0 && (
          <div style={s.block}>
            <div style={s.blockTitle}>Detected Fields</div>
            <div style={s.tagRow}>
              {fieldTypeCounts.map(t => (
                <span key={t.label} style={s.tag}>
                  <strong>{t.count}</strong> {t.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <div style={s.block}>
            <div style={s.blockTitle}>Warnings</div>
            <div style={s.noticeStack}>
              {warnings.map((w, i) => (
                <InlineNotice key={i} variant="warning" text={w} />
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div style={s.block}>
            <div style={s.blockTitle}>Recommendations</div>
            <ul style={s.list}>
              {recommendations.map((r, i) => (
                <li key={i} style={s.listItem}>{r}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Suggested Questions */}
        {suggestedQuestions.length > 0 && (
          <div style={s.block}>
            <div style={s.blockTitle}>Suggested Questions</div>
            <ul style={s.list}>
              {suggestedQuestions.map((q, i) => (
                <li key={i} style={s.listItem}>{q}</li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <button type="button" onClick={onStartChat} style={s.startBtn}>
          Start Chat
          <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

const s = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    width: '100%',
    padding: '20px 20px 40px',
    boxSizing: 'border-box',
    background: 'var(--bg, #f8f7f4)',
    animation: 'fadeIn 0.3s ease',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    background: 'transparent',
    border: 'none',
    padding: '8px 10px',
    marginBottom: 12,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-muted)',
    borderRadius: 8,
    transition: 'color 0.15s ease, background 0.15s ease',
  },
  backIcon: {
    fontSize: 13,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    width: '100%',
    maxWidth: 640,
    margin: '0 auto',
  },
  successCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    padding: '20px 22px',
    borderRadius: 16,
    background: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  successIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'rgba(34, 197, 94, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  successIcon: {
    color: 'var(--success)',
    fontSize: 20,
  },
  successTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: '0 0 4px',
    letterSpacing: '-0.02em',
    lineHeight: 1.3,
  },
  successSub: {
    fontSize: 14.5,
    color: 'var(--text-muted)',
    lineHeight: 1.5,
    margin: 0,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 12,
    padding: 18,
    borderRadius: 16,
    background: 'rgba(255,255,255,0.65)',
    border: '1px solid rgba(0,0,0,0.05)',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  metaLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  metaValue: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  block: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  blockTitle: {
    fontSize: 12.5,
    fontWeight: 700,
    color: 'var(--text-primary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tagRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 12px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(0,0,0,0.06)',
    fontSize: 13.5,
    color: 'var(--text-secondary)',
  },
  list: {
    margin: 0,
    padding: '0 0 0 4px',
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  listItem: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    lineHeight: 1.55,
    paddingLeft: 14,
    position: 'relative',
  },
  noticeStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  startBtn: {
    marginTop: 8,
    width: '100%',
    padding: '15px 24px',
    borderRadius: 14,
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    fontFamily: 'var(--font-main)',
    boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
};